import React from 'react';
import axios from 'src/axios';
import { UserContext } from 'src/store/ContextStore';
import { CONSTANTS } from 'src/components/Constants';

const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

// Matched against the path RELATIVE TO THE API BASE: '/pedigree/tags', not
// '/api/pedigree/tags'. Every call through src/axios passes a path relative to
// the instance's baseURL ('/api'), and axios puts that path — without the
// base — in config.url. These matchers used to require '/api/' (all but the
// FHQ one, which also accepted the bare form), so for six of the seven modules
// the interceptor below never matched a real request and blocked nothing.
// apiPathOf() now strips a leading '/api' when a caller does include it, so
// both forms match.
const MODULE_ROUTE_MATCHERS = {
  // Pedigrees & genomic records
  pedigrees: (url) => /^\/pedigree\b/.test(url),
  // Family History Questionnaire
  fhqs: (url) => /^\/questionnaire\b/.test(url),
  familyHistoryRecords: (url) => /^\/proband\b/.test(url),
  customForms: (url) => /^\/(forms|form-builder|workflows)\b/.test(url),
  pathways: (url) => /^\/pathways\b/.test(url),
  medicareBilling: (url) => /^\/medicare\b/.test(url),
  dataExplorer: (url) => /^\/data-explorer\b/.test(url)
};

// POST/PUT/DELETE requests under those prefixes that change no clinical record
// and no one else's data, so a read-only member keeps them: searches and
// queries that happen to be POSTs, an export, and the caller's own Data
// Explorer history, feedback and tile layout. The first four are the ones the
// backend's licence gate also lets a read-only workspace make
// (backend/services/licence/groupAccess.js READ_ONLY_ALLOWED). Without this
// list, fixing the matchers above would have started blocking Data Explorer
// queries and person search for every read-only member.
//
// The server refuses the same writes (backend/middleware/
// securityGroupReadOnlyGuard.js). Its let-through list for /form-builder is
// FORM_BUILDER_READ_ONLY_ALLOWED (backend/routes/form-builder.js), and for
// /medicare it has none. backend/__tests__/securityGroupReadOnlyRouters.test.js
// reads THIS list and fails if the /form-builder or /medicare entries here
// stop letting through exactly the same routes as the server. Keep one
// `['method', /regex/],` entry per line: that test reads it so.
const NOT_A_WRITE = [
  ['post', /^\/pedigree\/search-persons$/],
  ['post', /^\/data-explorer\/query$/],
  ['post', /^\/data-explorer\/nl-query$/],
  ['post', /^\/form-builder\/export\/csv\/[^/]+$/],
  ['post', /^\/data-explorer\/nl-recent$/],
  ['delete', /^\/data-explorer\/nl-recent\/[^/]+$/],
  ['post', /^\/data-explorer\/nl-feedback\/[^/]+$/],
  ['put', /^\/data-explorer\/(home-)?tile-config$/]
];

// config.url → the path relative to the API base, with any query string
// dropped. Absolute URLs are reduced to their pathname.
export function apiPathOf(rawUrl) {
  const raw = String(rawUrl || '');
  let path;
  try {
    path = new URL(raw, window.location.origin).pathname;
  } catch {
    path = raw.split('?')[0];
  }
  return path.replace(/^\/api(?=\/|$)/, '');
}

// Which read-only module, if any, a request would write to. `isReadOnly` is
// the provider's readOnly(moduleKey). Reads are never matched.
export function readOnlyModuleForRequest(method, rawUrl, isReadOnly) {
  const verb = String(method || 'get').toLowerCase();
  if (!WRITE_METHODS.includes(verb)) return null;
  const url = apiPathOf(rawUrl);
  if (NOT_A_WRITE.some(([m, re]) => m === verb && re.test(url))) return null;
  for (const [moduleKey, matcher] of Object.entries(MODULE_ROUTE_MATCHERS)) {
    if (isReadOnly(moduleKey) && matcher(url)) return moduleKey;
  }
  return null;
}

export const SgAccessContext = React.createContext({
  perms: null,
  allow: () => true,
  readOnly: () => false,
  canWrite: () => true
});

export default function SgAccessProvider({ children }) {
  const { user } = React.useContext(UserContext) || {};
  const isPrivileged = React.useMemo(
    () =>
      [
        CONSTANTS.userTypes.ADMIN,
        CONSTANTS.userTypes.GENETICS,
        CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
      ].includes(user?.userType),
    [user?.userType]
  );
  const isGeneticsUser = user?.userType === CONSTANTS.userTypes.GENETICS_USER;
  const perms = user?.securityGroupPermissions || null;

  const allow = React.useCallback(
    (moduleKey, action = 'read') => {
      if (isPrivileged) return true;
      if (!isGeneticsUser) return true;

      if (!perms) return true;
      if (!perms[moduleKey]) return false;

      if (action === 'write') return !perms.readOnly;

      return true;
    },
    [isPrivileged, isGeneticsUser, perms]
  );

  const readOnly = React.useCallback(
    (moduleKey) => {
      if (isPrivileged) return false;
      if (!isGeneticsUser) return false;
      if (!perms) return false;
      return !!perms[moduleKey] && !!perms.readOnly;
    },
    [isPrivileged, isGeneticsUser, perms]
  );

  const canWrite = React.useCallback(
    (moduleKey) => allow(moduleKey, 'write'),
    [allow]
  );

  React.useEffect(() => {
    if (!isGeneticsUser || isPrivileged) return;

    const interceptorId = axios.interceptors.request.use(
      (config) => {
        let moduleKey = null;
        try {
          moduleKey = readOnlyModuleForRequest(
            config?.method,
            config?.url,
            readOnly
          );
        } catch {
          // A malformed request is not ours to refuse.
        }
        if (!moduleKey) return config;

        const err = new Error(
          `Read-only access enabled for ${moduleKey} by your security group.`
        );
        err.response = {
          status: 403,
          data: {
            message: `Read-only access for ${moduleKey}`
          }
        };
        return Promise.reject(err);
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [isGeneticsUser, isPrivileged, readOnly]);

  const value = React.useMemo(
    () => ({ perms, allow, readOnly, canWrite }),
    [perms, allow, readOnly, canWrite]
  );

  return (
    <SgAccessContext.Provider value={value}>
      {children}
    </SgAccessContext.Provider>
  );
}
